import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { CORRELATION_ID_QUERY_PARAM_KEY } from '@zep/session-id';

import { useZepApiClient } from '../zep-api/ZepApiProvider';

import {
  type NormalizedEquipmentCatalog,
  normalizeEquipmentCatalog,
} from './normalize';
import type {
  EquipmentCatalogResponse,
  EquipmentServiceKey,
  InventoryResponse,
} from './types';

export const QUERY_KEY_EQUIPMENT_CATALOG = 'equipment-catalog';

const DEFAULT_EQUIPMENT_SERVICE: EquipmentServiceKey = 'core';

/**
 * Mirrors `useCurrentAvatarService` — quiz-app iframes append
 * `zepCorrelationId` to the play-app URL, and that key is the canonical
 * marker for "running inside quiz." We parse the search string directly
 * (not `useRouter`) to side-step hydration timing and re-render churn.
 */
export function useCurrentEquipmentService(): EquipmentServiceKey {
  if (typeof window === 'undefined') return DEFAULT_EQUIPMENT_SERVICE;
  const hasCorrelationId = new URLSearchParams(window.location.search).has(
    CORRELATION_ID_QUERY_PARAM_KEY,
  );
  return hasCorrelationId ? 'quiz' : DEFAULT_EQUIPMENT_SERVICE;
}

export function useEquipmentCatalog(
  service: EquipmentServiceKey = DEFAULT_EQUIPMENT_SERVICE,
  options?: {
    enabled?: boolean;
    suspense?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    inventory?: InventoryResponse | null;
  },
): UseQueryResult<NormalizedEquipmentCatalog> {
  const client = useZepApiClient();
  const { inventory: inventoryOpt, ...restOptions } = options ?? {};
  const inventory = inventoryOpt ?? null;

  return useQuery<EquipmentCatalogResponse, Error, NormalizedEquipmentCatalog>(
    [QUERY_KEY_EQUIPMENT_CATALOG, service],
    async () => {
      const { data } = await client.get(
        `/equipment/catalog?service=${service}`,
      );
      const envelope = data?.data ?? data;
      if (envelope == null) {
        throw new Error('Equipment catalog response was empty');
      }
      return envelope as EquipmentCatalogResponse;
    },
    {
      staleTime: 1000 * 60 * 30,
      useErrorBoundary: false,
      ...restOptions,
      select: raw => normalizeEquipmentCatalog(raw, { inventory }),
    },
  );
}
